<template>
  <div v-if="hasCarousels" :class="CMS_CONTENT_CLASS" :tabindex="rootTabindex">
    <template v-for="(section, index) in contentSections">
      <!-- Regular HTML content -->
      <VHtml
        v-if="section.type === 'html' && section.content"
        :key="`html-${index}`"
        ref="htmlSections"
        :html="section.content"
      />

      <!-- Product Carousel -->
      <CmsCarouselSection
        v-else-if="section.type === 'carousel'"
        :key="`carousel-${index}`"
        :products="section.products"
        :loading="section.loading"
        :class-name="section.className"
      />
    </template>
  </div>
  <VHtml v-else-if="html" ref="content" :class="CMS_CONTENT_CLASS" :tabindex="rootTabindex" :html="html" />
</template>

<script setup lang="ts">
import VHtml from '~/components/base/VHtml.vue'
import CmsCarouselSection from '~/components/content/CmsCarouselSection.vue'
import { UserRole } from '~/types/account'
import type { Demonstrator } from '~/types/account'
import { toInternalPath } from '~/utils/cmsLinks'
import { CMS_CONTENT_CLASS, firstChildEnd, restoreDeferredImages, withAccessibleVideos, withDeferredImages, withLazyImages, withoutLazyLoading } from '~/utils/cmsContent'
import { withVuetify4Classes } from '~/utils/cmsVuetifyClasses'
import { useReportsUrl } from '~/composables/environmentHelpers'
import { useCartStore } from '~/stores/cart'
import { useAuthStore } from '~/stores/auth'
import { useRootStore } from '~/stores/root'
import { useCmsCarousel } from '~/composables/useCmsCarousel'
import { useCmsVideoReveal } from '~/composables/useCmsVideoReveal'
import { PORTAL_REPORTS, PORTAL_DBWS_SEGMENT } from '~/utils/portalRoutes'

interface Props {
  htmlContent?: string | false
  hrefId?: string
  height?: string
  width?: string
  htmlLinks?: boolean
  // Below-the-fold content only: see withLazyImages.
  lazyImages?: boolean
  // The LCP image's content: an authored loading="lazy" is dropped, see withoutLazyLoading.
  eagerImages?: boolean
  // Images load after hydration (see withDeferredImages). Below-the-fold content
  // only. Implies lazyImages, whatever is deferred.
  deferImages?: boolean
  // With deferImages: a wrapper class. Only the images after that wrapper's first
  // child are deferred (see firstChildEnd); markup without it defers nothing.
  deferImagesAfterFirstChildOf?: string
  // tabindex="-1" on the root, so script can move focus to the content (e.g. an
  // anchor target) without adding it to the Tab order. Ported from nuxt2 PR 22948.
  focusable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  htmlLinks: true,
  lazyImages: false,
  eagerImages: false,
  deferImages: false,
  deferImagesAfterFirstChildOf: undefined,
  focusable: false,
  hrefId: undefined,
  height: undefined,
  width: undefined,
  htmlContent: ''
})

const { t } = useI18n()

const html = computed(() => {
  if (!props.htmlContent) {
    return props.htmlContent
  }
  // On the markup, so the server renders the grid classes too: see withVuetify4Classes.
  // The video widget's names likewise, so they are there before hydration. AB#65419
  const { deferImagesAfterFirstChildOf: wrapperClass } = props
  const htmlContent = withAccessibleVideos(
    withVuetify4Classes(props.eagerImages ? withoutLazyLoading(props.htmlContent) : props.htmlContent),
    t('video.clickNote')
  )
  if (!props.deferImages) {
    return props.lazyImages ? withLazyImages(htmlContent) : htmlContent
  }
  // An offset into htmlContent, so it is found and applied before any other rewrite.
  const from = wrapperClass ? firstChildEnd(htmlContent, wrapperClass) : 0
  const deferred = from === undefined ? htmlContent : withDeferredImages(htmlContent, from)
  // Deferred images are lazy too: restored after hydration, they would otherwise all load at once.
  return withLazyImages(deferred)
})
const rootTabindex = computed(() => props.focusable ? -1 : undefined)

const content = useTemplateRef('content')
const htmlSections = useTemplateRef('htmlSections')
const { contentSections, hasCarousels, parseAndFetchContent } = useCmsCarousel()
const { $appInsights } = useNuxtApp()
const { userRole, user } = useSAuthState()
const router = useRouter()

const authStore = useAuthStore()
const rootStore = useRootStore()
const cartStore = useCartStore()
const demonstrator = computed(() => cartStore.demonstrator)
// Deliberately NOT the `demonstrator` computed above: that is the storefront's
// owner (cart context), whereas the DBWS gate is about the signed-in user's own
// demonstrator record — the same thing nuxt2 read as `$auth.user.demonstrator`.
const signedInHasDbws = computed(() => !!(user.value?.demonstrator as Demonstrator | undefined)?.hasDbws)
const dbwsBillingLocation = {
  path: ACCOUNT_BILLING,
  query: { section: ACCOUNT_BILLING_SECTION_DBWS }
}
const isReportsRoute = (href: string) => href?.toLowerCase().includes(PORTAL_REPORTS)
const isDbwsRoute = (href: string) => href?.toLowerCase().includes(PORTAL_DBWS_SEGMENT)

useCmsVideoReveal()

const routeNeedsAuthentication = (checkRoute: string) => {
  return checkRoute.includes('account')
}

// Watch for HTML content changes and parse/fetch carousels. The composable
// publishes the parsed sections itself, before its product fetch, so they are in
// place by the time this payload renders. The promise is returned so a failure
// reaches Vue's error handler instead of going unhandled.
watch(html, value => parseAndFetchContent(value), { immediate: true })

// The elements whose markup came from the CMS: each HTML section in carousel
// mode, the single VHtml otherwise. Only these are processed.
// - Never a document-wide lookup of the CMS class: a page can put that class on
//   a wrapper (pages/index.vue does) and render several instances, and the
//   first match would then be someone else's content.
// - Never the sections wrapper either: it also holds the product carousels,
//   whose links belong to Vue components. Wiring those adds a second navigation
//   to every product card.
const getCmsRoots = (): HTMLElement[] => {
  const hosts = hasCarousels.value ? htmlSections.value ?? [] : [content.value]
  return hosts.map(host => host?.$el).filter((el): el is HTMLElement => !!el)
}

// Safe to key on the element: v-html replaces its nodes whenever the markup
// changes, so a link that survives a pass still has the href it was wired with,
// apart from the demoid that applyDemoId keeps current on data-add-demoid links.
const processedLinks = new WeakSet<HTMLAnchorElement>()

const DEMOID_LINK_ATTR = 'data-add-demoid'
const DEMOID_PARAM = 'demoid'
// data-add-demoid links (e.g. 'Join' CTAs) that processLinks let through its auth
// gate, each with the href it was wired with, so a credit can be undone.
const demoIdLinks = new WeakMap<HTMLAnchorElement, string>()

// The one demoid rule: what a link should carry right now, read from the live cart.
// Both the href and the SPA click use it, so a cleared demonstrator credits no one.
const demoIdFor = (a: HTMLAnchorElement) =>
  demoIdLinks.has(a) && demonstrator.value ? String(demonstrator.value.demoId) : null

// Keeps a data-add-demoid link's href in step with demoIdFor. searchParams keeps any
// existing query, puts demoid before the #fragment, and replaces a demoid already on
// the link rather than adding a second one. With no demonstrator the authored href
// comes back. Only http(s) URLs are credited.
const applyDemoId = (a: HTMLAnchorElement) => {
  const authored = demoIdLinks.get(a)
  if (authored === undefined) {
    return
  }
  const demoId = demoIdFor(a)
  if (!demoId) {
    a.setAttribute('href', authored)
    return
  }
  try {
    const url = new URL(authored, window.location.href)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return
    }
    url.searchParams.set(DEMOID_PARAM, demoId)
    a.setAttribute('href', url.toString())
  } catch {
    // Not a parseable URL: leave the link as authored.
  }
}

const getCmsLinks = () => getCmsRoots().flatMap(root => Array.from(root.querySelectorAll('a')))

// Handle link processing for both carousel and non-carousel modes
const processLinks = () => {
  if (!import.meta.client || !props.htmlLinks || !props.htmlContent) {
    return
  }

  const links = getCmsLinks()

  if (!links.length) {
    return
  }

  if (links.length > 1 && props.hrefId) {
    $appInsights.trackException({ exception: new Error(`Cms content apply href id ${props.hrefId} to more than one link.`) })
  }

  links.forEach((a: HTMLAnchorElement) => {
    // A pass can meet a link it has already wired: when the payload changes
    // but one section's markup does not, that section keeps its nodes. It must
    // not get a second click listener.
    if (processedLinks.has(a)) {
      return
    }
    processedLinks.add(a)
    const href = a.getAttribute('href')
    // Normalize site-relative CMS hrefs (no leading slash) to an absolute path. Left as-is
    // the browser resolves them against the CURRENT route, producing doubled paths like
    // /shop-products/shop-by/shop-products/shop-by/color that 404. Rewriting the attribute
    // also fixes open-in-new-tab / no-JS navigation, not just the SPA click handler below.
    const internalPath = toInternalPath(href)
    if (internalPath && internalPath !== href) {
      a.setAttribute('href', internalPath)
    }
    if (![UserRole.Demonstrator, UserRole.Customer].includes(userRole.value) && routeNeedsAuthentication(href ?? '')) {
      a.addEventListener('click', (event: Event) => {
        event.preventDefault()
        rootStore.redirectRoute = href ?? ''
        authStore.updateAuthMenu(true)
      })
      return
    }
    // A same-page #anchor is left alone: a demoid would make it a different
    // document URL, so a click would load a page instead of jumping.
    const linkHref = a.getAttribute('href')
    if (a.hasAttribute(DEMOID_LINK_ATTR) && linkHref && !linkHref.startsWith('#')) {
      demoIdLinks.set(a, linkHref)
      applyDemoId(a)
    }

    if (props.hrefId) {
      a.setAttribute('id', props.hrefId)
    }
    // External (absolute http/https) links must open in a new tab from code rather than
    // relying on a translator-supplied target attribute in the CMS/Lokalise string, which
    // can drift and silently drop the user out of the current flow (AB#60662).
    if (href && /^https?:\/\//i.test(href)) {
      a.setAttribute('target', '_blank')
      a.setAttribute('rel', 'noopener noreferrer')
    }
    if (internalPath) {
      a.addEventListener('click', async (event: Event) => {
        event.preventDefault()
        if (isReportsRoute(internalPath)) {
          // reports are in a different application so use full URL in new window
          if (internalPath.includes('?routeToPage=')) {
            const { reportsUrl } = useReportsUrl(internalPath.split('?routeToPage=')[1])
            openNewWindow(reportsUrl.value)
          } else {
            openNewWindow(useReportsUrl().reportsUrl.value)
          }
        } else if (isDbwsRoute(internalPath)) {
          // Client-side pre-check, mirroring the gate nuxt2's subscribe-now page
          // applied before opening anything. A demo without DBWS can never get a
          // redirect URL back, so without this they get a blank tab opened and
          // immediately closed plus a wasted round trip on the way to billing.
          // The server stays authoritative — GetDbwsRedirect returns "" for null
          // DBWS info, AccessLevel 0, or no payment account — this only removes
          // the flash.
          if (!signedInHasDbws.value) {
            router.push(dbwsBillingLocation)
            return
          }
          // Open the window synchronously, still inside the click's user-gesture
          // task, then point it at the redirect once it resolves. Safari only
          // honours window.open() during an immediate user gesture, so opening
          // after the await is silently blocked — and openNewWindow defers
          // through setTimeout, which breaks the gesture chain the same way.
          // Ported back from nuxt2 CmsContent, which had this workaround.
          const newWindow = window.open('about:blank', '_blank')
          try {
            const { redirectUrl } = await $api()<{redirectUrl: string}>('/account/demo/dbws/redirect', { method: 'post' })
            if (redirectUrl && newWindow) {
              newWindow.location.href = redirectUrl
            } else if (redirectUrl) {
              // Popup blocked outright, but the redirect is valid. nuxt2 dropped
              // the user on billing here; deliver them in the current tab
              // instead. Deliberate deviation — this whole branch exists for
              // browsers that interfere with window.open.
              window.location.href = redirectUrl
            } else {
              newWindow?.close()
              router.push(dbwsBillingLocation)
            }
          } catch {
            // nuxt2 fell back to the billing page on a failed redirect lookup;
            // without the catch the rejection escapes the click listener.
            newWindow?.close()
            router.push(dbwsBillingLocation)
          }
        } else {
          const demoId = demoIdFor(a)
          const query: Record<string, string> = demoId ? { [DEMOID_PARAM]: demoId } : {}
          router.push({ path: internalPath, hash: a.hash, query })
        }
      })
    }
  })
}

onMounted(() => {
  // From the rendered markup rather than one of its inputs: v-html replaces the
  // nodes whenever it changes (new content, or deferImages set for the same
  // content), and each replacement needs its classes, links and deferred images.
  watch(html, () => {
    // One pass per payload is enough in carousel mode too: its HTML sections are
    // published in the same tick as the payload changes, so they are rendered by
    // the time this runs. The carousels fill in later, and are not processed.
    processLinks()
    // After hydration this runs at the next idle moment.
    if (props.deferImages) {
      onNuxtReady(() => getCmsRoots().forEach(restoreDeferredImages))
    }
  }, {
    immediate: true,
    // On client-side navigation htmlContent changes before the v-html DOM is
    // patched; with the default 'pre' flush the processors would walk the
    // OUTGOING page's DOM and the new content's links would stay unwired.
    flush: 'post'
  })

  // A link is wired once, so one wired before the cart's demonstrator loaded
  // would otherwise never be credited, and one credited before it was cleared
  // would keep the old demoid.
  watch(() => demonstrator.value?.demoId, () => {
    if (!props.htmlLinks || !props.htmlContent) {
      return
    }
    getCmsLinks().filter(a => demoIdLinks.has(a)).forEach(applyDemoId)
  }, { flush: 'post' })
})

</script>

<style lang='scss' scoped>

  :deep() {
    // video widget: checkbox, label(cover-image) and video(i-frame)
    .video-container {
      position: relative;
      padding-bottom: 56.25%; /* 16:9 */
      margin-bottom: 16px;
      height: 0;
      // The player is laid out at full size from first paint: while it is
      // display:none (0x0) YouTube sizes its poster to the hidden player and
      // serves the 120x90 default.jpg, which is the blur once revealed. It sits
      // above the checkbox so the first click goes straight to the player.
      iframe { z-index: 2; }
      // With a cover image the player stays invisible until it takes focus
      // (useCmsVideoReveal). With no cover it is the only thing to see, so it
      // stays visible and shows YouTube's own poster.
      label ~ iframe { opacity: 0; }
      &.is-playing label ~ iframe { opacity: 1; }
      iframe,input[type="checkbox"],.cover {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
      input[type="checkbox"] {
        opacity: 0;
        z-index: 1;
      }
      input[type="checkbox"]:checked,
      input[type="checkbox"]:checked ~ label {
        display: none;
      }
      // Keyboard users can still toggle the checkbox, which reveals the player too.
      input[type="checkbox"]:checked ~ iframe {
        opacity: 1;
      }
    }
    // cloudinary video player styles
    .vjs-menu,
    .vjs-poster,
    .vjs-control-bar,
    .vjs-modal-dialog,
    .vjs-big-play-button,
    .vjs-loading-spinner,
    .vjs-text-track-display {
      display: none;
    }
    .vjs-fluid:not(.vjs-audio-only-mode) {
      padding-top: 0%;
    }
  }
</style>
